import { useState } from "react";
import { Calculator } from "lucide-react";
import { SectionHead, Reveal } from "@/components/public/home/ui";
import { useDemo } from "@/components/public/DemoDialog";

const HOURS_PER_CANDIDATE = 0.92;
const SLIDERS = [
  { k: "roles", label: "Open roles per month", min: 1, max: 50, step: 1, fmt: (v) => v, ticks: ["1 role", "25 roles", "50 roles"] },
  { k: "cands", label: "Candidates screened per role", min: 10, max: 150, step: 5, fmt: (v) => v, ticks: ["10 applicants", "75 applicants", "150 applicants"] },
  { k: "rate", label: "Blended hourly cost (recruiter / hiring manager)", min: 30, max: 200, step: 5, fmt: (v) => `$${v}`, ticks: ["$30/hr", "$100/hr", "$200/hr"] },
];

function Slider({ s, v, onChange }) {
  const pct = ((v - s.min) / (s.max - s.min)) * 100;
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={`roi-${s.k}`} className="text-sm font-medium text-slate-200">{s.label}</label>
        <span className="font-mono text-lg font-semibold text-indigo-300" data-testid={`roi-value-${s.k}`}>{s.fmt(v)}</span>
      </div>
      <input id={`roi-${s.k}`} type="range" min={s.min} max={s.max} step={s.step} value={v} onChange={(e) => onChange(+e.target.value)}
        data-testid={`roi-slider-${s.k}`} className="rs-range mt-4 w-full" style={{ "--pct": `${pct}%` }} />
      <div className="mt-2 flex justify-between text-[11px] font-mono text-slate-500">{s.ticks.map((t) => <span key={t}>{t}</span>)}</div>
    </div>
  );
}

export function ValueCalculator() {
  const { openDemo } = useDemo();
  const [v, setV] = useState({ roles: 10, cands: 35, rate: 75 });
  const hours = Math.round(v.roles * v.cands * HOURS_PER_CANDIDATE);
  const annual = hours * v.rate * 12;
  return (
    <section id="roi" className="relative bg-[#090D16] overflow-hidden scroll-mt-16" data-testid="roi-section">
      <div className="absolute inset-0 rs-grid-dark [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)]" />
      <div className="absolute -top-40 right-1/4 w-[520px] h-[520px] rounded-full bg-indigo-600/20 blur-[120px]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <SectionHead dark center eyebrow="Value calculator" title="Calculate your annual hiring ROI" sub="Adjust the sliders to project how many recruiter and hiring-manager hours your organisation saves every month." />
        <Reveal className="mt-12 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6 sm:p-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-9">
            {SLIDERS.map((s) => <Slider key={s.k} s={s} v={v[s.k]} onChange={(x) => setV({ ...v, [s.k]: x })} />)}
          </div>
          <div className="lg:col-span-5 rounded-2xl border border-white/10 bg-[#0E1422] p-6 sm:p-8 text-center" data-testid="roi-results">
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-400">Projected annual savings</div>
            <div className="mt-3 text-4xl sm:text-5xl font-extrabold text-emerald-400 font-mono tracking-tight" data-testid="roi-annual-savings">${annual.toLocaleString("en-US")}</div>
            <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 gap-4 text-left">
              <div><div className="text-[11px] text-slate-400 font-mono uppercase">Hours saved / mo</div><div className="text-2xl font-bold text-white font-mono mt-0.5" data-testid="roi-hours-saved">{hours.toLocaleString("en-US")} hrs</div></div>
              <div><div className="text-[11px] text-slate-400 font-mono uppercase">Velocity gain</div><div className="text-2xl font-bold text-cyan-400 font-mono mt-0.5" data-testid="roi-velocity">86% faster</div></div>
            </div>
            <button onClick={openDemo} data-testid="roi-btn-claim" className="mt-8 w-full py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold shadow-lg shadow-indigo-900/50 inline-flex items-center justify-center gap-2 transition-colors">
              <Calculator className="w-4 h-4" /> Claim your efficiency gains
            </button>
          </div>
        </Reveal>
        <p className="mt-5 text-xs text-slate-500 text-center max-w-3xl mx-auto" data-testid="roi-assumptions">Estimate only. Assumes ~55 minutes of recruiter and hiring-manager time saved per screened candidate (first-round call, notes and assessment review), and time-to-shortlist cut from about 7 days to 1.</p>
      </div>
    </section>
  );
}
